import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { api, fileUrl, API } from "../lib/api";
import { Download, ArrowLeft, FileText, ArrowUpRight } from "lucide-react";

function isImage(ext) {
  return ["png", "jpg", "jpeg", "webp", "gif", "svg"].includes((ext || "").toLowerCase());
}
function isVideo(ext) {
  return ["mp4", "webm", "mov"].includes((ext || "").toLowerCase());
}

export default function AssetDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get(`/files/${id}`)
      .then((r) => setItem(r.data))
      .catch(() => setErr("Not found"));
  }, [id]);

  if (err) return <div className="p-12 text-center text-muted-fg">{err}</div>;
  if (!item) return <div className="p-12 text-center text-muted-fg">Loading…</div>;

  const downloadUrl = `${API}/files/${item.id}/download`;
  const showImg = isImage(item.extension);
  const showVid = isVideo(item.extension);

  return (
    <main className="min-h-[calc(100vh-72px)] py-12">
      <div className="mx-auto max-w-[1200px] px-5 sm:px-8">
        <Link to="/browse" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-fg hover:text-ink">
          <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" />
          {t("browse")}
        </Link>

        <div className="mt-6 grid gap-10 lg:grid-cols-[1.4fr,1fr]">
          <div className="overflow-hidden card">
            <div className="relative aspect-video w-full" style={{ background: "hsl(var(--surface-2))" }}>
              {showImg ? (
                <img src={fileUrl(item.url)} alt={item.title} className="h-full w-full object-contain" />
              ) : showVid ? (
                <video src={fileUrl(item.url)} controls className="h-full w-full" />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-accent text-[hsl(var(--accent-fg))] shadow-xl">
                    <FileText className="h-12 w-12" />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <span className="chip chip-accent">FREE · {item.extension?.toUpperCase()}</span>
            <h1 className="font-display mt-4 text-4xl leading-[0.95] sm:text-5xl" data-testid="asset-title">
              {item.title}
            </h1>
            <div className="mt-2 text-xs uppercase tracking-[0.14em] text-muted-fg">
              {t("by")} <span className="text-ink">{item.owner_name}</span>
            </div>

            <p className="mt-6 text-base text-ink/80">{item.description || "—"}</p>

            <dl className="mt-8 grid grid-cols-2 gap-3">
              <Field label="Type" value={item.extension?.toUpperCase() || "—"} />
              <Field label="Size" value={`${(item.size / 1024 / 1024).toFixed(2)} MB`} />
              <Field label={t("category")} value={item.category} />
              <Field label={t("downloads")} value={item.download_count} />
            </dl>

            <a href={downloadUrl} className="btn btn-accent mt-8 w-full !py-3.5" data-testid="asset-download-btn">
              <Download className="h-4 w-4" />
              {t("download")}
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </main>
  );
}

function Field({ label, value }) {
  return (
    <div className="card p-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-fg">{label}</div>
      <div className="font-display mt-1 text-xl text-ink">{value}</div>
    </div>
  );
}
